/**
 * The company gallery routes can be restricted to a set of SKUs, either by id
 * ({@code ?skuIds=1,2,3}) or by Canva brand template tag
 * ({@code ?skuTags=revshop-1,revshop-test2-2}), which the API resolves to ids.
 */
export interface GallerySkuFilter {
  skuIds?: number[];
  skuTags?: string[];
}

/** Comma separated values or repeated params are both accepted. */
const readValues = (search: URLSearchParams, name: string): string[] =>
  search
    .getAll(name)
    .flatMap((value) => value.split(','))
    .map((value) => value.trim())
    .filter(Boolean);

/**
 * Reads the {@code ?skuIds=} / {@code ?skuTags=} filter off a location search
 * string. A missing, blank or fully invalid value means "no filter", matching
 * the API, which ignores an empty list.
 */
export const parseGallerySkuFilter = (search: string): GallerySkuFilter => {
  const params = new URLSearchParams(search);
  const skuIds = readValues(params, 'skuIds')
    .map(Number)
    .filter((id) => Number.isInteger(id) && id > 0);
  const skuTags = readValues(params, 'skuTags');
  return {
    ...(skuIds.length ? { skuIds: Array.from(new Set(skuIds)) } : {}),
    ...(skuTags.length ? { skuTags: Array.from(new Set(skuTags)) } : {}),
  };
};

export const hasGallerySkuFilter = (filter: GallerySkuFilter): boolean =>
  Boolean(filter.skuIds?.length || filter.skuTags?.length);

/** Serialises the filter as the query the gallery APIs expect. */
export const gallerySkuFilterQuery = (filter: GallerySkuFilter): string =>
  [
    filter.skuIds?.length ? `skuIdIn=${filter.skuIds.join(',')}` : '',
    filter.skuTags?.length
      ? `skuTagIn=${filter.skuTags.map(encodeURIComponent).join(',')}`
      : '',
  ]
    .filter(Boolean)
    .join('&');

/**
 * Serialises the filter back into the URL form the gallery routes read, as opposed to the API
 * form {@link gallerySkuFilterQuery} produces. Used to return a buyer to the same restricted
 * gallery they arrived on.
 */
export const gallerySkuFilterSearch = (filter: GallerySkuFilter): string =>
  [
    filter.skuIds?.length ? `skuIds=${filter.skuIds.join(',')}` : '',
    filter.skuTags?.length
      ? `skuTags=${filter.skuTags.map(encodeURIComponent).join(',')}`
      : '',
  ]
    .filter(Boolean)
    .join('&');

/** Stable identity for a filter, for use as a React key or effect dependency. */
export const gallerySkuFilterKey = (filter: GallerySkuFilter): string =>
  gallerySkuFilterQuery(filter);
